import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { Box, Button, IconButton, TextField } from "@material-ui/core";
import { ChangeEvent, useEffect, useState } from "react";

import ConfirmationPopover from "../../components/ConfirmationPopover";
import CustomCard from "../../components/CustomCard";
import { cancellationPoliciesCollRef } from "../../utils/referencies";
import Loader from "../../components/Loader";
import { useNotify } from "react-admin";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import { applyCreateTimestamp, applyUpdateTimestamp } from "../../utils/timestamp";
import { DocReference, DocumentSnapshot } from "../../types/firebase.types";
import StepperCancellationPolicies from "./StepperCancellationPolicies";
import CashCancelation from "./CashCancelation";

interface CancellationPoliciesInterface {
  cancelTime: string;
  charge: string;
  id: number | string;
  ref: DocReference | null;
  updatedAt: Date | null;
  createdAt: Date | null;
}

const INTIAL_VALUE = {
  cancelTime: "",
  charge: "",
  id: 12345,
  ref: null,
  updatedAt: null,
  createdAt: null,
};

const CancellationPolicies = () => {
  const notify = useNotify();

  const [loadingFetching, setLoadingFetching] = useState(true);
  const [cancellationPolicies, setCancellationPolicies] = useState<CancellationPoliciesInterface[]>([INTIAL_VALUE]);
  const [firstSlot, setFirstSlot] = useState<CancellationPoliciesInterface>(INTIAL_VALUE);
  const [isValidCancellationPolicies, setIsValidCancellationPolicies] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { disabledStyles } = useDisabledOnLoading(actionLoading);

  const addNewcancellationPolicyHandler = () => {
    setCancellationPolicies((prevState) => {
      return [...prevState, INTIAL_VALUE];
    });
  };

  const removecancellationPolicyHandler = async (policyData: CancellationPoliciesInterface) => {
    if (policyData.ref) {
      setActionLoading(true);
      try {
        await policyData.ref.delete();
        notify("Cancellation policy deleted", "success");
        setActionLoading(false);
      } catch (ex: any) {
        console.log(ex.message);
        notify("Error on deleting Cancellation policy", "error");
        setActionLoading(false);
      }
    }

    setCancellationPolicies((prevState) => {
      return prevState.filter((policy) => policy.id !== policyData.id);
    });
  };

  const handleChangeCancellationPolicy = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    isCharge = true,
    isFirst = false
  ) => {
    const regex = /^[0-9]{1,3}$|^[0-9.0-9]{1,5}$/;

    if (isFirst) {
      if (regex.test(e.target.value) || !e.target.value) {
        setFirstSlot((prevState) => ({ ...prevState, charge: e.target.value }));
      }
    } else {
      if (regex.test(e.target.value) || !e.target.value)
        setCancellationPolicies((prevState) => {
          let selectedPolicy = prevState[index];
          selectedPolicy = isCharge
            ? { ...selectedPolicy, charge: e.target.value }
            : { ...selectedPolicy, cancelTime: e.target.value };
          return [...prevState.slice(0, index), selectedPolicy, ...prevState.slice(index + 1)];
        });
    }
  };

  const fetchcaccellationPolicies = async () => {
    try {
      const snapshots = await cancellationPoliciesCollRef.orderBy("cancelTime", "asc").get();
      if (snapshots.size) {
        const snapshotDocs = snapshots.docs.map((doc: DocumentSnapshot) => {
          return {
            ...doc.data(),
            cancelTime: (doc.data()?.cancelTime / 60).toString(),
            charge: doc.data()?.charge.toString(),
            ref: doc.ref,
            id: doc.id,
          };
        });
        setFirstSlot(snapshotDocs[0] as CancellationPoliciesInterface);
        const spreadSnapshotDocs = snapshotDocs.slice(1);
        setCancellationPolicies(spreadSnapshotDocs as CancellationPoliciesInterface[]);
      }
      setLoadingFetching(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingFetching(false);
    }
  };

  const validateCancellationPolicies = () => {
    const checkIsValidPolicies = (policy: CancellationPoliciesInterface) => {
      const { cancelTime, charge } = policy;
      const isValidCancelTime = cancellationPolicies.filter((policy) => policy.cancelTime === cancelTime);
      return (
        cancelTime &&
        Number(cancelTime) >= 0 &&
        charge &&
        Number(charge) >= 0 &&
        Number(charge) <= 100 &&
        isValidCancelTime.length === 1
      );
    };
    const isValidPolicies = cancellationPolicies.every(checkIsValidPolicies);
    setIsValidCancellationPolicies(!isValidPolicies || Number(firstSlot.charge) < 0 || !firstSlot.charge);
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    try {
      for (const policy of cancellationPolicies) {
        const { ref, id, ...finalCancellationPolicy } = policy;
        ref
          ? await ref.update(
              applyUpdateTimestamp({
                ...finalCancellationPolicy,
                cancelTime: +finalCancellationPolicy.cancelTime * 60,
                charge: +finalCancellationPolicy.charge,
              })
            )
          : await cancellationPoliciesCollRef.add(
              applyCreateTimestamp({
                ...finalCancellationPolicy,
                cancelTime: +finalCancellationPolicy.cancelTime * 60,
                charge: +finalCancellationPolicy.charge,
              })
            );
      }
      setActionLoading(false);
      notify("cancellation policy has been creating/updating successfully", "success");
    } catch (ex: any) {
      console.log(ex.message);
      setActionLoading(false);
      notify("Error on creating/updating cancellation policy", "error");
    }
  };

  useEffect(() => {
    fetchcaccellationPolicies();
  }, []);

  useEffect(() => {
    validateCancellationPolicies();
  }, [cancellationPolicies, firstSlot]);

  return (
    <Loader loading={loadingFetching}>
      <Box style={disabledStyles}>
        <CashCancelation />
        <Box style={{ paddingInline: "1em" }}>
          <CustomCard width={"50%"} title="Online Cancel Policies">
            <Box display="flex" alignItems="center" mb={2} paddingTop={1}>
              <TextField
                variant="outlined"
                size="small"
                label="Cancel Time (Hr)"
                value="0"
                style={{ width: "45%", marginRight: 20 }}
                inputProps={{
                  min: 0,
                }}
                disabled
              />
              <TextField
                variant="outlined"
                size="small"
                label="Charge (%)"
                value={firstSlot.charge}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeCancellationPolicy(e, 0, true, true)}
                style={{ width: "45%" }}
                inputProps={{
                  min: 0,
                  max: 100,
                }}
              />
            </Box>

            {cancellationPolicies.map((policy: CancellationPoliciesInterface, index: number) => {
              return (
                <Box key={policy.id} display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Cancel Time (Hr)"
                    value={policy.cancelTime}
                    onChange={(e) => handleChangeCancellationPolicy(e, index, false)}
                    style={{ width: "45%" }}
                    inputProps={{
                      min: 0,
                    }}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Charge (%)"
                    value={policy.charge}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeCancellationPolicy(e, index)}
                    style={{ width: "45%" }}
                    inputProps={{
                      min: 0,
                      max: 100,
                    }}
                  />
                  <Box width={50}>
                    <ConfirmationPopover
                      onConfirm={() => removecancellationPolicyHandler(policy)}
                      message="Are you sure you want to delete this policy?"
                      confirmationText="Confirm"
                      confirmationColor="secondary"
                      Icon={
                        <IconButton size="small">
                          <HighlightOffIcon />
                        </IconButton>
                      }
                    />
                  </Box>
                </Box>
              );
            })}
            <Box mt={3}>
              <Button
                color="secondary"
                onClick={addNewcancellationPolicyHandler}
                disabled={isValidCancellationPolicies}
              >
                + Add One
              </Button>
            </Box>
          </CustomCard>

          <StepperCancellationPolicies steps={[firstSlot, ...cancellationPolicies]} />

          <Box mt={1}>
            <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isValidCancellationPolicies}>
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Loader>
  );
};

export default CancellationPolicies;
